import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  FC,
  KeyboardEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { MouseEvent as ReactMouseEvent } from "react";
import {
  AppSettingsTabProps,
  ConditionOperator,
  ConditionExpression,
  TOnboardingConfig,
} from "../../models";
import { ConditionEditorDialog } from "./ConditionEditorDialog";
import { ConditionPreview } from "./ConditionPreview";
import { formatConditionInline } from "./conditionHelpers";

const CONDITION_NAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]*$/;
const INVALID_NAME_HELPER =
  "Condition name must start with a letter and use only letters, numbers, or underscores.";
const DUPLICATE_NAME_HELPER = "Condition name must be unique.";
const DEFAULT_NAMED_CONDITION: ConditionExpression = {
  field: "field_name",
  op: ConditionOperator.EXISTS,
};

type ConditionsMap = NonNullable<TOnboardingConfig["conditions"]>;

function getNextConditionName(conditions: ConditionsMap): string {
  let nextIndex = 1;

  while (
    Object.prototype.hasOwnProperty.call(conditions, `newCondition${nextIndex}`)
  ) {
    nextIndex += 1;
  }

  return `newCondition${nextIndex}`;
}

function buildConfigWithConditions(
  prev: TOnboardingConfig,
  nextConditions: Record<string, ConditionExpression>,
): TOnboardingConfig {
  if (Object.keys(nextConditions).length === 0) {
    const { conditions: _removed, ...rest } = prev;
    return rest;
  }

  return {
    ...prev,
    conditions: nextConditions,
  };
}

function renameConditionKey(
  conditions: ConditionsMap,
  currentName: string,
  nextName: string,
): ConditionsMap {
  return Object.fromEntries(
    Object.entries(conditions).map(([name, value]) =>
      name === currentName ? [nextName, value] : [name, value],
    ),
  );
}

const ConditionRow: FC<{
  condition: ConditionExpression;
  config: TOnboardingConfig;
  expanded: boolean;
  existingNames: string[];
  name: string;
  onDelete: (name: string) => void;
  onRename: (currentName: string, nextName: string) => void;
  onSaveCondition: (name: string, nextCondition: ConditionExpression) => void;
  onToggle: (name: string) => void;
}> = memo(({
  condition,
  config,
  expanded,
  existingNames,
  name,
  onDelete,
  onRename,
  onSaveCondition,
  onToggle,
}) => {
  const [nameDraft, setNameDraft] = useState(name);
  const [nameError, setNameError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const summary = useMemo(() => formatConditionInline(condition), [condition]);

  useEffect(() => {
    setNameDraft(name);
    setNameError(null);
  }, [name]);

  const toggle = useCallback(() => {
    onToggle(name);
  }, [name, onToggle]);

  const stop = useCallback((event: ReactMouseEvent<HTMLElement>) => {
    event.stopPropagation();
  }, []);

  const commitName = useCallback(() => {
    const trimmedName = nameDraft.trim();

    if (trimmedName === name) {
      setNameDraft(name);
      setNameError(null);
      return;
    }

    if (!CONDITION_NAME_PATTERN.test(trimmedName)) {
      setNameError(INVALID_NAME_HELPER);
      return;
    }

    if (
      existingNames.some(
        (existingName) =>
          existingName !== name && existingName === trimmedName,
      )
    ) {
      setNameError(DUPLICATE_NAME_HELPER);
      return;
    }

    setNameError(null);
    onRename(name, trimmedName);
  }, [existingNames, name, nameDraft, onRename]);

  const handleNameKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== "Enter") {
        return;
      }

      event.preventDefault();
      commitName();
    },
    [commitName],
  );

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        onClick={toggle}
        sx={{
          py: 0.75,
          px: 0.5,
          cursor: "pointer",
          borderRadius: 1,
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <IconButton
          size="small"
          sx={{
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
          aria-label={
            expanded ? `Collapse condition ${name}` : `Expand condition ${name}`
          }
          onClick={(event) => {
            stop(event);
            toggle();
          }}
        >
          <ExpandMoreIcon fontSize="small" />
        </IconButton>

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ minWidth: 0, flex: 1 }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: "text.primary",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              flexShrink: 0,
              maxWidth: 240,
            }}
          >
            {name}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              minWidth: 0,
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontFamily: "Menlo, Monaco, Consolas, monospace",
            }}
          >
            {summary}
          </Typography>
        </Stack>

        <IconButton
          aria-label={`Delete condition ${name}`}
          onClick={(event) => {
            stop(event);
            onDelete(name);
          }}
          size="small"
          sx={{
            color: (theme) => alpha(theme.palette.error.main, 0.45),
            "&:hover": {
              color: "error.main",
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.12),
            },
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Collapse in={expanded} unmountOnExit>
        <Stack spacing={1.5} sx={{ pt: 1, pb: 2, pl: 4 }}>
          <TextField
            label="Name"
            value={nameDraft}
            onChange={(event) => setNameDraft(event.target.value)}
            onBlur={commitName}
            onKeyDown={handleNameKeyDown}
            error={!!nameError}
            helperText={nameError ?? undefined}
            size="small"
            fullWidth
            inputProps={{
              "aria-label": `Condition name ${name}`,
            }}
          />

          <ConditionPreview
            condition={condition}
            onEdit={() => setEditing(true)}
            title="Body"
          />
        </Stack>
      </Collapse>

      {editing ? (
        <ConditionEditorDialog
          availableFields={[]}
          condition={condition}
          open
          title={`Edit condition ${name}`}
          validationConfig={config}
          validationMode="namedCondition"
          validationPath={`/conditions/${name}`}
          onClose={() => setEditing(false)}
          onSave={(nextCondition) => {
            onSaveCondition(name, nextCondition);
            setEditing(false);
          }}
        />
      ) : null}
    </Box>
  );
});

export const ConditionsSection: FC<{
  conditions: TOnboardingConfig["conditions"];
  onConfigChange: AppSettingsTabProps["onConfigChange"];
  validationConfig: TOnboardingConfig;
}> = memo(({ conditions, onConfigChange, validationConfig }) => {
  const [expandedConditions, setExpandedConditions] = useState<
    Record<string, boolean>
  >({});
  const entries = useMemo(() => Object.entries(conditions ?? {}), [conditions]);
  const conditionNames = useMemo(() => entries.map(([name]) => name), [entries]);

  const toggleCondition = useCallback((name: string) => {
    setExpandedConditions((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  }, []);

  const handleAddCondition = useCallback(() => {
    onConfigChange((prev) => {
      const currentConditions = (prev.conditions ?? {}) as ConditionsMap;
      const nextName = getNextConditionName(currentConditions);

      return buildConfigWithConditions(prev, {
        ...currentConditions,
        [nextName]: DEFAULT_NAMED_CONDITION,
      });
    });
  }, [onConfigChange]);

  const handleRenameCondition = useCallback(
    (currentName: string, nextName: string) => {
      const currentConditions = (conditions ?? {}) as ConditionsMap;

      if (
        currentName === nextName
        || !Object.prototype.hasOwnProperty.call(currentConditions, currentName)
        || Object.prototype.hasOwnProperty.call(currentConditions, nextName)
      ) {
        return;
      }

      setExpandedConditions((prevExpanded) => {
        const nextExpanded = { ...prevExpanded };

        if (prevExpanded[currentName]) {
          nextExpanded[nextName] = true;
        }

        delete nextExpanded[currentName];

        return nextExpanded;
      });

      onConfigChange((prev) => {
        const nextConditions = (prev.conditions ?? {}) as ConditionsMap;

        if (
          currentName === nextName
          || !Object.prototype.hasOwnProperty.call(nextConditions, currentName)
          || Object.prototype.hasOwnProperty.call(nextConditions, nextName)
        ) {
          return prev;
        }

        return buildConfigWithConditions(
          prev,
          renameConditionKey(nextConditions, currentName, nextName),
        );
      });
    },
    [conditions, onConfigChange],
  );

  const handleDeleteCondition = useCallback(
    (name: string) => {
      setExpandedConditions((prev) => {
        if (!prev[name]) {
          return prev;
        }

        const nextExpanded = { ...prev };
        delete nextExpanded[name];
        return nextExpanded;
      });

      onConfigChange((prev) => {
        const currentConditions = (prev.conditions ?? {}) as ConditionsMap;

        if (!Object.prototype.hasOwnProperty.call(currentConditions, name)) {
          return prev;
        }

        return buildConfigWithConditions(
          prev,
          Object.fromEntries(
            Object.entries(currentConditions).filter(
              ([currentName]) => currentName !== name,
            ),
          ),
        );
      });
    },
    [onConfigChange],
  );

  const handleSaveCondition = useCallback(
    (name: string, nextCondition: ConditionExpression) => {
      onConfigChange((prev) => {
        const currentConditions = (prev.conditions ?? {}) as ConditionsMap;

        if (!Object.prototype.hasOwnProperty.call(currentConditions, name)) {
          return prev;
        }

        return buildConfigWithConditions(prev, {
          ...currentConditions,
          [name]: nextCondition,
        });
      });
    },
    [onConfigChange],
  );

  return (
    <Paper
      sx={{
        padding: "20px 32px",
        background: "#ffffff",
        borderRadius: "14px",
        border: "none",
      }}
    >
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Named conditions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Reusable JSON-authored conditions that fields can reference with
            <Box component="span" sx={{ fontFamily: "Menlo, Monaco, Consolas, monospace", mx: 0.5 }}>
              op: &quot;condition&quot;
            </Box>
            from field-level condition bodies.
          </Typography>
        </Stack>

        {entries.length > 0 &&
          entries.map(([name, condition]) => (
            <ConditionRow
              key={name}
              condition={condition}
              config={validationConfig}
              expanded={!!expandedConditions[name]}
              existingNames={conditionNames}
              name={name}
              onDelete={handleDeleteCondition}
              onRename={handleRenameCondition}
              onSaveCondition={handleSaveCondition}
              onToggle={toggleCondition}
            />
          ))}

        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddCondition}
          sx={{ alignSelf: "flex-start" }}
        >
          Add condition
        </Button>
      </Stack>
    </Paper>
  );
});
